import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { authMe, authLogout } from "@/lib/apiClient";

/**
 * AuthContext — exposes the current user (or the DEMO fallback) plus helpers.
 *
 * The backend always returns a user from /api/auth/me — either the real JWT-
 * authenticated user or the shared DEMO user. That means `user` is never null
 * after boot, simplifying every consumer. Use `user.is_demo` to branch UI.
 */
const AuthContext = createContext({
  user: null,
  loading: true,
  refresh: () => {},
  logout: () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const me = await authMe();
      setUser(me);
    } catch (err) {
      // 401 — anonymous request in lockdown mode. Render the login gate.
      // Other errors (network etc.) — treat same; user has to retry login.
      // Logging at debug level so prod consoles stay quiet but devs can trace.
      if (typeof console !== "undefined" && console.debug) {
        console.debug("[auth] refresh failed:", err?.response?.status || err?.message || err);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authLogout();
    } catch (err) {
      // Best-effort cookie clear — log at debug level for diagnostics.
      if (typeof console !== "undefined" && console.debug) {
        console.debug("[auth] logout failed:", err?.message || err);
      }
    }
    await refresh();
  }, [refresh]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Stable context value — prevents every consumer from re-rendering on each
  // AuthProvider render due to a fresh object identity.
  const value = useMemo(
    () => ({ user, loading, refresh, logout }),
    [user, loading, refresh, logout],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
